/**
 * CalendarForecastBanner — banner sopra la griglia calendario quando esiste
 * una proiezione completa di fine mese. Mostra: consuntivo a oggi + forecast
 * residuo + totale proiettato. Se c'è un budget mensile nello scope mostra
 * anche il delta vs budget così l'AM capisce subito se sta finendo sopra o
 * sotto target.
 *
 * Nessun rendering quando `month_total` è null o `projected_total` è null
 * (storico troppo corto): preferiamo silenzio a numeri parziali.
 *
 * Estratto da `CalendarTab.js` il 2026-05-29 per ridurre la cc del file
 * principale. API/data-testid invariati.
 */
import React from 'react';
import { Info } from 'lucide-react';
import { fmt } from '../lib/calendarFormat';


export function CalendarForecastBanner({ forecast, monthlyBudget }) {
  const mt = forecast?.month_total;
  if (!mt || mt.projected_total == null) return null;

  const projected = mt.projected_total;
  const budgetSales = monthlyBudget?.total_sales;
  const deltaPct = budgetSales && budgetSales > 0
    ? ((projected - budgetSales) / budgetSales) * 100
    : null;
  const deltaCls = deltaPct == null
    ? 'text-slate-500'
    : deltaPct >= 0 ? 'text-emerald-700' : 'text-red-700';

  return (
    <div
      className="bg-slate-50 border border-dashed border-slate-300 px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-2 text-sm"
      data-testid="forecast-banner"
    >
      <div className="flex items-center gap-1.5 text-xs text-slate-500 uppercase tracking-wider font-semibold">
        <span className="w-2 h-2 rounded-full bg-slate-400" />
        Proiezione fine mese
        <Info
          className="w-3.5 h-3.5 text-slate-400 cursor-help"
          aria-label="Come è calcolato il forecast"
          data-testid="forecast-banner-info"
          title={
            "Forecast: media degli stessi giorni della settimana (lun vs lun, sab vs sab) "
            + "delle ultime 8 settimane. Esclude giorni a 0/None (chiusure). Si nasconde "
            + "quando lo storico è troppo corto per essere affidabile."
          }
        />
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 font-mono tabular-nums">
        <span>
          <span className="text-slate-500">Consuntivo:</span>{' '}
          <span className="font-semibold">{fmt(mt.actual_to_date, 'currency')}</span>
        </span>
        <span>
          <span className="text-slate-500">+ Forecast:</span>{' '}
          <span className="font-semibold italic text-slate-600">{fmt(mt.forecast_remaining, 'currency')}</span>
        </span>
        <span>
          <span className="text-slate-500">= Totale:</span>{' '}
          <span className="font-bold" data-testid="forecast-banner-projected">{fmt(projected, 'currency')}</span>
        </span>
        {deltaPct != null && (
          <span data-testid="forecast-banner-delta">
            <span className="text-slate-500">vs Budget:</span>{' '}
            <span className={`font-semibold ${deltaCls}`}>
              {deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(1)}%
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
